import React, {
  ReactElement,
  Reducer,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";

import {
  StreetSweepingSchedule,
  GoogleMapCoordinate,
  SweeperTruckGpsData,
  MapAnimationPathCoordinate,
} from "../models";
import { useInterval } from "../hooks";
import { GoogleMapsContext } from "../contexts";

import TruckMarkerIconSVG from "../assets/img/svg/map-marker.svg";

declare global {
  interface Window {
    google: {
      maps: any;
    };
  }
}

interface StreetMapState {
  center: GoogleMapCoordinate;
  zoom: number;
  pathCoordinates: Array<MapAnimationPathCoordinate> | null;
}

enum MapActionType {
  pathLoaded = "pathLoaded",
  streetFocus = "streetFocus",
  zoom = "zoom",
  reset = "reset",
}

type StreetMapAction =
  | {
      type: MapActionType.pathLoaded;
      payload: { pathCoordinates: Array<MapAnimationPathCoordinate> };
    }
  | {
      type: MapActionType.streetFocus;
      payload: { center: GoogleMapCoordinate };
    }
  | { type: MapActionType.zoom; payload: any }
  | { type: MapActionType.reset };

const GOOGLE_MAPS_DEFAULT_ZOOM = 19;
const GOOGLE_MAPS_DEFAULT_MAP_CENTER: GoogleMapCoordinate = {
  lat: -33.9150691,
  lng: 151.0361782,
};
const TRUCK_MARKER_DEFAULT_VELOCITY = 10;
const STREET_MAP_INITIAL_STATE = {
  center: GOOGLE_MAPS_DEFAULT_MAP_CENTER,
  zoom: GOOGLE_MAPS_DEFAULT_ZOOM,
  pathCoordinates: null,
};

export default function SweeperMap({
  currentStreet,
  truckGpsData,
}: {
  readonly currentStreet: StreetSweepingSchedule | undefined;
  readonly truckGpsData: Array<SweeperTruckGpsData> | undefined;
}): ReactElement {
  const { isGoogleMapLoaded, geocodeStreet } = useContext(GoogleMapsContext);
  const mapRef = useRef<GoogleMap>(null);

  const [mapState, dispatchMapAction] = useReducer<
    Reducer<StreetMapState, StreetMapAction>
  >((prevState, action) => {
    switch (action.type) {
      case MapActionType.pathLoaded:
        const { pathCoordinates } = action.payload;
        return {
          ...prevState,
          zoom: 15,
          pathCoordinates,
        };
      case MapActionType.streetFocus:
        return {
          ...prevState,
          center: action.payload.center,
        };
      case MapActionType.zoom:
        return { ...prevState };
      case MapActionType.reset:
        return STREET_MAP_INITIAL_STATE;
      default:
        throw new Error();
    }
  }, STREET_MAP_INITIAL_STATE);

  const getTruckMapActivityData = (
    pathCoordinates: ReadonlyArray<SweeperTruckGpsData>
  ): Array<MapAnimationPathCoordinate> => {
    let mapData: MapAnimationPathCoordinate[] = [];

    for (let i = 0; i < pathCoordinates.length; i++) {
      const currentPoint = pathCoordinates[i];
      if (i === 0) {
        mapData.push({
          ...currentPoint,
          distanceInMeter: 0,
          timeInSec: 0,
          velocity: 0,
        });
      }

      const point1 = currentPoint;
      const latLng1 = new window.google.maps.LatLng(point1.lat, point1.lng);

      const point2 = pathCoordinates[i + 1];
      if (!point2) break;
      const latLng2 = new window.google.maps.LatLng(point2.lat, point2.lng);

      const segmentDistance = window.google.maps.geometry.spherical.computeDistanceBetween(
        latLng1,
        latLng2
      );
      const segmentTime = (point2.epochTime - point1.epochTime) / 1000;
      const velocity = segmentDistance / segmentTime;

      const lastCoordinate = mapData[mapData.length - 1];
      const distanceInMeter = lastCoordinate.distanceInMeter + segmentDistance;
      const timeInSec = lastCoordinate.timeInSec + segmentTime;

      mapData.push({ ...currentPoint, distanceInMeter, timeInSec, velocity });
    }

    return mapData;
  };

  useEffect(() => {
    if (currentStreet) {
      geocodeStreet(currentStreet).then(({ lat, lng }) => {
        dispatchMapAction({
          type: MapActionType.streetFocus,
          payload: {
            center: { lat, lng },
          },
        });
      });

      if (truckGpsData && truckGpsData.length > 0) {
        setAnimationCounter(1);
        dispatchMapAction({
          type: MapActionType.pathLoaded,
          payload: {
            pathCoordinates: getTruckMapActivityData(truckGpsData),
          },
        });
      }
    }

    // return () => {
    //   dispatchMapAction({ type: MapActionType.reset });
    // };
  }, [currentStreet, truckGpsData, geocodeStreet]);

  // ---- Marker Animation
  const [animationCounter, setAnimationCounter] = useState(0);
  const [markerProgress, setMarkerProgress] = useState<GoogleMapCoordinate[]>(
    []
  );

  const initialDate = useMemo(() => {
    if (animationCounter && truckGpsData) {
      setMarkerProgress([]);
    }
    return new Date();
  }, [animationCounter, truckGpsData]);

  const moveTruckMarker = useCallback(() => {
    if (mapState.pathCoordinates) {
      const timeDiffInSeconds = (+new Date() - +initialDate) / 1000;
      const markerDistance = timeDiffInSeconds * TRUCK_MARKER_DEFAULT_VELOCITY; // d = v*t

      let progress = mapState.pathCoordinates.filter(
        (point) => point.distanceInMeter < markerDistance
      );
      const nextPoint = mapState.pathCoordinates.find((coordinate) => {
        return coordinate.distanceInMeter > markerDistance;
      });

      if (!nextPoint) {
        setAnimationCounter((prevState) => prevState + 1);
        return;
      }
      const previousPoint = progress[progress.length - 1];

      const previousPointLatLng = new window.google.maps.LatLng(
        previousPoint.lat,
        previousPoint.lng
      );
      const nextPointLatLng = new window.google.maps.LatLng(
        nextPoint.lat,
        nextPoint.lng
      );

      const deltaDistanceInMeter =
        nextPoint.distanceInMeter - previousPoint.distanceInMeter;
      const percentage =
        (markerDistance - previousPoint.distanceInMeter) / deltaDistanceInMeter;
      const position = window.google.maps.geometry.spherical.interpolate(
        previousPointLatLng,
        nextPointLatLng,
        percentage
      );

      progress = progress.concat(position);
      setMarkerProgress(progress);
    }
  }, [initialDate, mapState.pathCoordinates]);

  useInterval(() => {
    moveTruckMarker();
  }, 200);

  const { center, zoom, pathCoordinates } = mapState;

  return (
    <>
      {isGoogleMapLoaded && (
        <GoogleMap
          mapContainerClassName="tracker-section__map"
          ref={mapRef}
          center={center}
          zoom={zoom}
          options={{
            minZoom: zoom,
            draggable: false,
            scrollwheel: false,
            clickable: false,
            zoomControl: true,
            mapTypeControl: false,
            scaleControl: false,
            streetViewControl: false,
            rotateControl: false,
            fullscreenControl: false,
          }}
        >
          {pathCoordinates ? (
            <>
              <Polyline
                path={pathCoordinates}
                options={{ strokeColor: "#60c3ad", strokeWeight: 4 }}
              />
              {markerProgress.length > 0 && (
                <Marker
                  position={markerProgress[markerProgress.length - 1]}
                  icon={{
                    url: TruckMarkerIconSVG,
                    scaledSize: { height: 60, width: 60 },
                    anchor: { x: 30, y: 20 },
                  }}
                />
              )}
              <Marker position={center} />
            </>
          ) : (
            <Marker position={center} />
          )}
        </GoogleMap>
      )}
    </>
  );
}
